import { useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";
import { GripVertical, Loader2, MessageSquarePlus, Pencil, Trash2, X } from "lucide-react";
import { splitNarrative } from "@/lib/narrative";
import type { AdminDriver, Matchup } from "../types";

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  published: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  resolved: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  voided: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

function DriverAvatar({ driver }: { driver: AdminDriver | undefined }) {
  if (!driver) return <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-bold">?</span>;
  const ring = `0 0 0 2px #${driver.team_colour ?? "666"}`;
  return driver.headshot_url ? (
    <img src={driver.headshot_url} alt={driver.name_acronym} className="h-10 w-10 shrink-0 rounded-full object-cover" style={{ boxShadow: ring }} />
  ) : (
    <span
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-bold"
      style={{ backgroundColor: `#${driver.team_colour ?? "666"}22`, boxShadow: ring }}
    >
      {driver.name_acronym}
    </span>
  );
}

const MatchupCard = ({
  matchup,
  driverA,
  driverB,
  isGenerating,
  actionsDisabled,
  onQueueNote,
  onClearNote,
  onEdit,
  invalidateAll,
}: {
  matchup: Matchup;
  driverA: AdminDriver | undefined;
  driverB: AdminDriver | undefined;
  isGenerating: boolean;
  actionsDisabled: boolean;
  onQueueNote: () => void;
  onClearNote: () => void;
  onEdit: () => void;
  invalidateAll: () => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: matchup.id });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const { title, body } = splitNarrative(matchup.narrative);
  const queuedNote = matchup.pending_feedback;
  const isQueued = queuedNote !== null && queuedNote !== undefined;

  const handleDelete = async () => {
    setBusy(true);
    try {
      const { error } = await supabase.rpc("admin_delete_matchup", { p_matchup_id: matchup.id });
      if (error) throw error;
      toast({ title: "Matchup deleted" });
      invalidateAll();
    } catch (e) {
      toast({ title: "Error", description: (e as Error).message, variant: "destructive" });
    } finally {
      setBusy(false);
      setDeleteOpen(false);
    }
  };

  const disabled = actionsDisabled || busy || isGenerating;

  return (
    <Card
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`relative overflow-hidden ${isDragging ? "z-10 shadow-lg" : ""} ${
        isQueued ? "border-l-4 border-l-amber-500" : ""
      }`}
    >
      {isGenerating && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/70">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      )}
      <CardContent className="space-y-3 p-4">
        {/* Header: number + drivers, status top-right */}
        <div className="flex items-center gap-2">
          <button
            className="touch-none cursor-grab p-1 text-muted-foreground active:cursor-grabbing"
            {...attributes}
            {...listeners}
            aria-label="Drag to reorder"
          >
            <GripVertical className="h-4 w-4" />
          </button>
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-bold">
            {matchup.display_order ?? "—"}
          </span>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <DriverAvatar driver={driverA} />
            <span className="text-xs text-muted-foreground">vs</span>
            <DriverAvatar driver={driverB} />
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <Badge className={`text-[10px] ${STATUS_COLORS[matchup.status ?? "draft"]}`}>{matchup.status ?? "draft"}</Badge>
          </div>
        </div>

        {/* Subheader + content: full narrative, always visible */}
        {(title || body) && (
          <div className="space-y-1 pl-8">
            {title && <p className="text-sm font-bold">{title}</p>}
            {body && <p className="text-xs leading-relaxed text-muted-foreground">{body}</p>}
          </div>
        )}

        {isQueued && (
          <div className="ml-8 flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5">
            <MessageSquarePlus className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
            <p className="flex-1 break-words text-[11px] text-amber-700 dark:text-amber-300">
              {queuedNote?.trim() ? queuedNote : "Queued for retry"}
            </p>
            <button
              className="shrink-0 text-amber-600 hover:text-amber-500 disabled:opacity-50 dark:text-amber-400"
              disabled={disabled}
              onClick={onClearNote}
              aria-label="Remove from queue"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        <div className="flex justify-end gap-0.5">
          <Button
            size="icon"
            variant="ghost"
            className={`h-10 w-10 ${isQueued ? "text-amber-600 dark:text-amber-400" : ""}`}
            disabled={disabled}
            onClick={onQueueNote}
          >
            <MessageSquarePlus className="h-4 w-4" />
          </Button>
          <Button size="icon" variant="ghost" className="h-10 w-10" disabled={disabled} onClick={onEdit}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button size="icon" variant="ghost" className="h-10 w-10 text-destructive" disabled={disabled} onClick={() => setDeleteOpen(true)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this matchup?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone. Any picks made for this matchup will be orphaned.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default MatchupCard;
